import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { realtimeService } from '../services/api';
import { POLL_INTERVAL_FAST_MS } from '../constants/theme';

const RerunContext = createContext(null);

const RERUN_KEY = 'qa-rerunning-project-id';

export const RerunProvider = ({ children }) => {
  const [rerunningProjectId, setRerunningProjectIdRaw] = useState(() => {
    return localStorage.getItem(RERUN_KEY) || null;
  });
  const refreshProjectsRef = useRef(null);
  const unsubscribeRef = useRef(null);

  // Wrapped setter that persists to localStorage
  const setRerunningProjectId = useCallback((id) => {
    if (id) localStorage.setItem(RERUN_KEY, id);
    else localStorage.removeItem(RERUN_KEY);
    setRerunningProjectIdRaw(id);
  }, []);

  const registerRefresh = useCallback((cb) => {
    refreshProjectsRef.current = cb;
    return () => { refreshProjectsRef.current = null; };
  }, []);

  const startRerunSubscription = useCallback((projectId) => {
    if (unsubscribeRef.current) unsubscribeRef.current();
    unsubscribeRef.current = realtimeService.subscribeToProjectStatus(
      projectId,
      (status) => {
        refreshProjectsRef.current?.();
        const total = status.totalPages || 0;
        const withResults = (status.passedPages || 0) + (status.failedPages || 0);
        const processing = status.processingPages || 0;
        const pending = status.pendingPages || 0;
        const allDone = total > 0 && withResults >= total && processing === 0 && pending === 0;
        if (allDone) {
          localStorage.removeItem(RERUN_KEY);
          setRerunningProjectIdRaw(null);
          if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
          }
        }
      },
      POLL_INTERVAL_FAST_MS
    );
  }, []);

  // On mount, restore subscription if a rerun was in progress before reload
  useEffect(() => {
    if (rerunningProjectId) {
      startRerunSubscription(rerunningProjectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  useEffect(() => () => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
  }, []);

  return (
    <RerunContext.Provider value={{
      rerunningProjectId,
      setRerunningProjectId,
      registerRefresh,
      startRerunSubscription,
    }}>
      {children}
    </RerunContext.Provider>
  );
};

export const useRerunContext = () => {
  const ctx = useContext(RerunContext);
  if (!ctx) throw new Error('useRerunContext must be used within RerunProvider');
  return ctx;
};
